import { deleteData, getData, postData, updateData } from "@/request/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/lib/toast";

export const useAddToCart = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (product: any) => {
      const response = await postData(`cart`, product);
      return response.data;
    },
    onSuccess: (res) => {
      if (res?.type === "warning") {
        toast.warning(res.message || "Warning adding property to cart");
        return;
      }

      queryClient.invalidateQueries({ queryKey: ["cart"] });
      toast.success(res.message || "Property added to cart successfully");
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Error adding property to cart"
      );
    },
  });
};

export const useGetCart = () => {
  return useQuery({
    queryKey: ["cart"],
    queryFn: async () => {
      const response = await getData(`cart`);
      return response.data;
    },
    retry: 2,
    refetchInterval: 60 * 60 * 1000, // 1 hour
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
  });
};

export const useClearCart = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (itemId: any) => {
      const response = await deleteData(`cart/${itemId}`);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res?.message || "Cart cleared successfully");
      queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Error clearing cart");
    },
  });
};

export const usePayment = () => {
  return useMutation({
    mutationFn: async (data: any) => {
      const response = await postData(`payment`, data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res?.message || "Payment processed successfully");
    },
    onError: (error: any) => {
      if (error?.response?.data?.identityRequired) return; // handled by the checkout gate
      toast.error(
        error?.response?.data?.error || error?.response?.data?.message || "Error processing payment"
      );
    },
  });
};

/* buyer identity (national ID / iqama) — stored encrypted server-side */
export const useSaveIdentity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { idNumber: string; birthDate?: string }) => {
      const response = await updateData(`user/identity`, data);
      return response.data;
    },
    onSuccess: (res) => {
      toast.success(res?.message || "Identity saved securely");
      queryClient.invalidateQueries({ queryKey: ["current-user"] });
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Could not save identity");
    },
  });
};

export const useGetWallet = ({ page = 1, limit = 10 }) => {
  return useQuery({
    queryKey: ["wallet", page, limit],
    queryFn: async () => {
      const response = await getData(`wallet?page=${page}&limit=${limit}`);
      return response.data;
    },
    retry: 2,
    refetchInterval: 60 * 60 * 1000, // 1 hour
    staleTime: 60 * 60 * 1000 * 10,
    gcTime: 60 * 60 * 1000 * 10,
  });
};
